import { db } from '@/firebase'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore'

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase()
}

function pickUsername(authUser, data) {
  return (
    String(data?.username || '').trim() ||
    String(authUser?.displayName || '').trim() ||
    String(authUser?.email || '').split('@')[0] ||
    'مستخدم'
  )
}

/** البحث عن مستخدم بالإيميل أو user_id (UID) — doc.id قد يختلف عن user_id */
export async function findMenuCustomerUser(authUser) {
  const uid = String(authUser?.uid || '').trim()
  const email = normalizeEmail(authUser?.email)
  if (!uid && !email) return null

  if (uid) {
    try {
      const byUid = await getDocs(
        query(collection(db, 'users'), where('user_id', '==', uid), limit(5))
      )
      if (!byUid.empty) {
        const d = byUid.docs[0]
        return { id: d.id, ...d.data() }
      }
    } catch {
      /* fallthrough */
    }
  }

  if (email) {
    try {
      const byEmail = await getDocs(
        query(collection(db, 'users'), where('email', '==', email), limit(5))
      )
      if (!byEmail.empty) {
        const d = byEmail.docs[0]
        return { id: d.id, ...d.data() }
      }
    } catch {
      /* fallthrough */
    }
  }

  return null
}

export async function createMenuCustomerUser(authUser, cityName = '') {
  const uid = String(authUser?.uid || '').trim()
  const email = normalizeEmail(authUser?.email)
  if (!uid || !email) throw new Error('بيانات تسجيل الدخول غير مكتملة')

  const payload = {
    email,
    user_id: uid,
    username: pickUsername(authUser, null),
    profile_img: String(authUser?.photoURL || '').trim(),
    phone: [],
    addresses: [],
    selectedRegion: String(cityName || '').trim(),
    createdAt: serverTimestamp()
  }

  const ref = await addDoc(collection(db, 'users'), payload)
  return { id: ref.id, ...payload }
}

export async function ensureMenuCustomerUser(authUser, cityName = '') {
  const existing = await findMenuCustomerUser(authUser)
  if (existing) return existing
  return createMenuCustomerUser(authUser, cityName)
}

export function normalizeUserPhones(phoneField) {
  if (!Array.isArray(phoneField)) return []
  return phoneField
    .map((entry) => {
      if (entry && typeof entry === 'object') {
        const phoneid = String(entry.phoneid || entry.phone || '').trim()
        if (!phoneid) return null
        return { phoneid, name: String(entry.name || '').trim() }
      }
      const phoneid = String(entry || '').trim()
      return phoneid ? { phoneid, name: '' } : null
    })
    .filter(Boolean)
}

export function normalizeUserAddresses(addressesField) {
  if (!Array.isArray(addressesField)) return []
  return addressesField
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const addressid = String(entry.addressid || '').trim()
      if (!addressid) return null
      return {
        addressid,
        lat: String(entry.lat ?? '').trim(),
        long: String(entry.long ?? '').trim(),
        name: String(entry.name || '').trim()
      }
    })
    .filter(Boolean)
}

export async function appendUserPhone(userDocId, phoneEntry) {
  const phoneid = String(phoneEntry?.phoneid || '').trim()
  if (!userDocId || !phoneid) return

  const ref = doc(db, 'users', userDocId)
  const snap = await getDoc(ref)
  const data = snap.exists() ? snap.data() : {}
  const phones = normalizeUserPhones(data.phone)
  if (phones.some((p) => p.phoneid === phoneid)) return

  phones.push({ phoneid, name: String(phoneEntry?.name || '').trim() })
  await updateDoc(ref, { phone: phones })
}

export async function appendUserAddress(userDocId, addressEntry) {
  const addressid = String(addressEntry?.addressid || '').trim()
  if (!userDocId || !addressid) return

  const ref = doc(db, 'users', userDocId)
  const snap = await getDoc(ref)
  const data = snap.exists() ? snap.data() : {}
  const addresses = normalizeUserAddresses(data.addresses)
  const exists = addresses.some((a) => a.addressid === addressid)
  if (exists) return

  addresses.push({
    addressid,
    lat: String(addressEntry?.lat ?? '').trim(),
    long: String(addressEntry?.long ?? '').trim(),
    name: String(addressEntry?.name || '').trim()
  })
  await updateDoc(ref, { addresses })
}
